<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoDataIntervalo from '@/components/base/campos/CampoDataIntervalo.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { formatarDataBR, isVencido } from '@/utils/useDataBR'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'
import { fornecedorSearch } from '@/search-definitions/fornecedorSearch'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])
const authStore = useAuthStore()
const { obterClasseStatusGhost } = useUI()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const filtros = reactive({
  buscaLivre: '',
  idProduto: '',
  nomeProduto: '',
  idFornecedor: '',
  nomeFornecedor: '',
  lstStatusLoteFabricacao: '',
  isBloqueadoParaUso: '',
  dataFabricacaoInicio: '',
  dataFabricacaoFim: '',
  dataValidadeInicio: '',
  dataValidadeFim: ''
})

const listas = reactive({ LstStatusLoteFabricacao: [] })

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const temPermissaoCompleta = computed(() => authStore.pode('LOTE_FABRICACAO_COMPLETO'))
const temPermissaoVisualizacao = computed(() => authStore.pode('LOTE_FABRICACAO_VISUALIZACAO'))
const podeVerAcoes = computed(() => temPermissaoCompleta.value || temPermissaoVisualizacao.value)

const configBuscaProduto = computed(() => ({
  ...criarConfigBuscaProduto(),
  titulo: 'Buscar Produto',
  filtrosFixos: { idEmpresa: authStore.empresaAtiva }
}))

const configBuscaFornecedor = computed(() => ({
  ...fornecedorSearch,
  titulo: 'Buscar Fornecedor',
  filtrosFixos: {
    ...fornecedorSearch.filtrosFixos,
    idEmpresa: authStore.empresaAtiva
  }
}))

const colunas = computed(() => {
  const cols = [
    { field: 'idLoteFabricacao', label: 'ID', width: 80, sortable: true, slotName: 'id' },
    { field: 'nomeProduto', label: 'Produto', sortable: true, class: 'fw-medium text-body-emphasis' },
    { field: 'nomeFornecedor', label: 'Fornecedor', sortable: true },
    { field: 'numeroLoteFornecedor', label: 'Nº Lote', sortable: true, class: 'fw-medium' },
    { field: 'dataFabricacao', label: 'Data Fab.', align: 'center', sortable: true, slotName: 'dataFabricacao' },
    { field: 'dataValidade', label: 'Data Val.', align: 'center', sortable: true, slotName: 'dataValidade' },
    { field: 'isBloqueadoParaUso', label: 'Bloqueado?', align: 'center', sortable: true, slotName: 'bloqueado' },
    { field: 'lstStatusLoteFabricacao', label: 'Status', align: 'center', sortable: true, slotName: 'status' }
  ]
  if (podeVerAcoes.value) {
    cols.push({ field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false })
  }
  return cols
})

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstStatusLoteFabricacao: (row) => obterNomeStatus(row.lstStatusLoteFabricacao)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => { buscarDados(1) }

watch(() => authStore.empresaAtiva, (n) => { if (n) buscarDados(1); else { lista.value = []; paginacao.value.TotalCount = 0 } })

async function carregarListas () {
  try {
    const res = await api.get('/ListasCliente/lookup/LST_ST_LOTE_FABRICACAO')
    listas.LstStatusLoteFabricacao = res.data
  } catch (e) {
    try {
      const res = await api.get('/ListasSistema/lookup/LST_ST_LOTE_FABRICACAO')
      listas.LstStatusLoteFabricacao = res.data
    } catch (e2) { console.warn('Erro ao carregar listas de status de lote:', e2) }
  }
}

function obterCorBloqueado (valor) { return valor ? 'badge-ghost-danger' : 'badge-ghost-success' }
function obterTextoBloqueado (valor) { return valor ? 'Sim' : 'Não' }
function obterCorStatusLote (id) { return obterClasseStatusGhost(obterNomeStatus(id)) }
function obterNomeStatus (id) {
  if (!id) return '-'
  const obj = listas.LstStatusLoteFabricacao.find(s => s.idListaSistemaItem === id || s.idListaClienteItem === id)
  return obj ? obj.textoLista : id
}

async function buscarDados (pageNumber = 1) {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }
  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)
    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (filtros.buscaLivre) params.append('buscaLivre', filtros.buscaLivre)
    if (filtros.idProduto) params.append('idProduto', filtros.idProduto)
    if (filtros.idFornecedor) params.append('idFornecedor', filtros.idFornecedor)
    if (filtros.lstStatusLoteFabricacao) params.append('lstStatusLoteFabricacao', filtros.lstStatusLoteFabricacao)
    if (filtros.isBloqueadoParaUso !== '') params.append('isBloqueadoParaUso', filtros.isBloqueadoParaUso)
    if (filtros.dataFabricacaoInicio) params.append('dataFabricacaoInicio', filtros.dataFabricacaoInicio)
    if (filtros.dataFabricacaoFim) params.append('dataFabricacaoFim', filtros.dataFabricacaoFim)
    if (filtros.dataValidadeInicio) params.append('dataValidadeInicio', filtros.dataValidadeInicio)
    if (filtros.dataValidadeFim) params.append('dataValidadeFim', filtros.dataValidadeFim)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/LotesFabricacao?${params.toString()}`)
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar os dados.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally { loading.value = false }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => {
  Object.keys(filtros).forEach(k => { filtros[k] = '' })
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}
const mudarTamanhoPagina = (n) => { paginacao.value.PageSize = n; buscarDados(1) }
const editarRegistro = (item) => { emit('editar-item', item) }
const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idLoteFabricacao
  auditoriaSelecionada.nome = item.numeroLoteFornecedor || 'Lote #' + item.idLoteFabricacao
  mostrarAuditoria.value = true
}

onMounted(async () => {
  await carregarListas()
  if (authStore.empresaAtiva || !authStore.isAdminInterno) buscarDados(1)
})

onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })
defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Lotes de Fabricação"
    subtitulo="Controle de Lotes de Produtos"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    label-botao-adicionar="Novo Lote"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label">Busca Livre (Nº Lote)</label>
          <input type="text" class="form-control" v-model="filtros.buscaLivre" placeholder="Buscar por número de lote..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-4">
          <CampoBusca id="filtroProduto" label="Produto" v-model="filtros.idProduto" :configuracao-busca="configBuscaProduto" @selecionado="val => filtros.nomeProduto = val.nome" />
        </div>
        <div class="col-md-4">
          <CampoBusca id="filtroFornecedor" label="Fornecedor" v-model="filtros.idFornecedor" :configuracao-busca="configBuscaFornecedor" @selecionado="val => filtros.nomeFornecedor = val.nomeFantasia" />
        </div>
        <div class="col-md-3">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatusLoteFabricacao" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="s in (listas.LstStatusLoteFabricacao || [])" :key="s.idListaSistemaItem || s.idListaClienteItem" :value="s.idListaSistemaItem || s.idListaClienteItem">{{ s.textoLista }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label">Bloqueado?</label>
          <select class="form-select" v-model="filtros.isBloqueadoParaUso" @change="aplicarFiltros">
            <option :value="''">Todos</option>
            <option :value="true">Sim</option>
            <option :value="false">Não</option>
          </select>
        </div>
        <div class="col-md-3">
          <CampoDataIntervalo id="filtroFabricacao" label="Período Fabricação" v-model:modelValueInicio="filtros.dataFabricacaoInicio" v-model:modelValueFim="filtros.dataFabricacaoFim" />
        </div>
        <div class="col-md-3">
          <CampoDataIntervalo id="filtroValidade" label="Período Validade" v-model:modelValueInicio="filtros.dataValidadeInicio" v-model:modelValueFim="filtros.dataValidadeFim" />
        </div>
        <div class="col-md-3 ms-auto text-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum lote de fabricação encontrado."
      row-key="idLoteFabricacao"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarRegistro($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idLoteFabricacao }}</span>
      </template>
      <template #dataFabricacao="{ row }">{{ formatarDataBR(row.dataFabricacao) }}</template>

      <template #dataValidade="{ row }">
        <span :class="{'text-danger fw-bold': isVencido(row.dataValidade)}">
          {{ formatarDataBR(row.dataValidade) }}
        </span>
      </template>

      <template #bloqueado="{ row }">
        <span class="badge" :class="obterCorBloqueado(row.isBloqueadoParaUso)">
          {{ obterTextoBloqueado(row.isBloqueadoParaUso) }}
        </span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterCorStatusLote(row.lstStatusLoteFabricacao)">
          {{ obterNomeStatus(row.lstStatusLoteFabricacao) }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon" @click="editarRegistro(row)" :title="temPermissaoCompleta ? 'Editar' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-primary' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="LoteFabricacao"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
</style>
